/**
 * The API speaks the fleet's wall clock: "2026-10-20T18:59:14" means 18:59 in the zone the
 * API runs in (APP_TIMEZONE — Asia/Riyadh), whatever zone the browser is in. These helpers
 * keep that straight:
 *   - `wallClock(value)` — a Date whose UTC fields ARE that wall clock (format it with
 *     timeZone 'UTC' to print exactly what the API sent);
 *   - `fleetInstant(value, zone)` — the real moment, for "12 s ago";
 *   - `fleetNow`/`fleetToday`/`toFleetParam` — values to SEND as filters.
 */

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?$/;

/** The API's date-time (or date) as a Date whose UTC fields are the fleet wall clock. */
export function wallClock(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = PATTERN.exec(value);
  if (!m) return null;
  const ms = m[7] ? Number(m[7].padEnd(3, '0').slice(0, 3)) : 0;
  const d = new Date(Date.UTC(2000, Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0), ms));
  d.setUTCFullYear(Number(m[1]));
  return d;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(zone, f);
  }
  return f;
}

/** The wall clock of `zone` at `instant`, as a UTC-fields Date. */
export function toWallClock(instant: Date, zone: string): Date {
  const p: Record<string, number> = {};
  for (const part of partsFormatter(zone).formatToParts(instant)) if (part.type !== 'literal') p[part.type] = Number(part.value);
  const d = new Date(Date.UTC(2000, p.month - 1, p.day, p.hour, p.minute, p.second, instant.getUTCMilliseconds()));
  d.setUTCFullYear(p.year);
  return d;
}

/** The real moment of an API date-time, read in the fleet's zone. */
export function fleetInstant(value: string | null | undefined, zone: string): Date | null {
  const wall = wallClock(value);
  if (!wall) return null;
  // offset = wall − instant; one correction pass handles a DST edge
  let instant = wall.getTime() - (toWallClock(wall, zone).getTime() - wall.getTime());
  instant = wall.getTime() - (toWallClock(new Date(instant), zone).getTime() - instant);
  return new Date(instant);
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** "2026-10-20" — today in the fleet's zone. */
export function fleetToday(zone: string, now: Date = new Date()): string {
  const w = toWallClock(now, zone);
  return `${pad(w.getUTCFullYear(), 4)}-${pad(w.getUTCMonth() + 1)}-${pad(w.getUTCDate())}`;
}

/** "2026-10-20T18:59:14" for a UTC-fields wall-clock Date — the API's parameter format. */
export function toFleetParam(wall: Date): string {
  return `${pad(wall.getUTCFullYear(), 4)}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:${pad(wall.getUTCSeconds())}`;
}

/** "2026-10-20" ± days. */
export function addDays(date: string, days: number): string {
  const d = wallClock(date);
  if (!d) return date;
  d.setUTCDate(d.getUTCDate() + days);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
